/* eslint-disable complexity */
import React from 'react';
import {
    Admin,
    CustomRoutes,
    Resource,
} from 'react-admin';

import { Navigate, Route } from 'react-router-dom';

import { Permissions } from '@src/global/permissions';
import { getFirstAllowedPath } from '@src/custom/LayoutCustom/MenuCustom';

import Error404 from '@src/components/error/Error404';

import './app.scss';
import { AuthLoader, useAuth } from './auth/auth';
import SessionInactivityCheck from './auth/sessionInactivityCheck';
import dataProvider from './dataProvider/dataProvider';

import { LayoutCustom } from '@src/custom';
import { Taxonomies } from '@src/global/taxonomies';
import i18nProvider from './i18nProvider';
import { lightTheme, darkTheme } from './theme';

const Error401 = React.lazy(() => import('@src/components/error/Error401'));
const ImportAdmins = React.lazy(() => import('@src/components/users/admins/Import'));
const ImportHomeOfficeEmployees = React.lazy(() => import('@src/components/users/home_office_employees/Import'));
const SessionsSettingEdit = React.lazy(() => import('@src/components/sessions_setting/edit'));
const SearchWeightsEdit = React.lazy(() => import('@src/components/search_weights/edit'));

const BannersList = React.lazy(() => import('@src/components/banners/List'));
const EditBanner = React.lazy(() => import('@src/components/banners/Edit'));

const AttachmentsList = React.lazy(() => import('@src/components/attachments/list'));
const EditAttachment = React.lazy(() => import('@src/components/attachments/edit'));

const TrackedDownloadsList = React.lazy(() => import('@src/components/tracked_downloads/list'));

const BusinessUnitsList = React.lazy(() => import('@src/components/business_units/list'));

const ProductUpdateList = React.lazy(() => import('@src/components/product_updates/list'));
const EditProductUpdate = React.lazy(() => import('@src/components/product_updates/edit'));
const CreateProductUpdate = React.lazy(() => import('@src/components/product_updates/create'));

const ImpactingPolicyList = React.lazy(() => import('@src/components/impacting_policies/list'));
const EditImpactingPolicy = React.lazy(() => import('@src/components/impacting_policies/edit'));
const CreateImpactingPolicy = React.lazy(() => import('@src/components/impacting_policies/create'));

const PlatformsList = React.lazy(() => import('@src/components/platforms/list'));
const EditPlatform = React.lazy(() => import('@src/components/platforms/edit'));

const ProductAvailabilityList = React.lazy(() => import('@src/components/product_availability_map/list'));
const EditProductAvailability = React.lazy(() => import('@src/components/product_availability_map/edit'));

const ProductPagesList = React.lazy(() => import('@src/components/product_pages/list'));
const CreateProductPage = React.lazy(() => import('@src/components/product_pages/create'));
const EditProductPage = React.lazy(() => import('@src/components/product_pages/edit'));

const SearchPhraseCreate = React.lazy(() => import('@src/components/search_phrases/create'));
const SearchPhraseEdit = React.lazy(() => import('@src/components/search_phrases/edit'));
const SearchPhraseList = React.lazy(() => import('@src/components/search_phrases/list'));
const SearchPhraseShow = React.lazy(() => import('@src/components/search_phrases/show'));

const SearchTermsList = React.lazy(() => import('@src/components/search_terms/list'));

const MatchingSearchCreate = React.lazy(() => import('@src/components/matching_searches/create'));
const MatchingSearchEdit = React.lazy(() => import('@src/components/matching_searches/edit'));
const MatchingSearchList = React.lazy(() => import('@src/components/matching_searches/list'));

const CarrierCommissionsList = React.lazy(() => import('@src/components/CarrierCommissions/List'));
const CarrierCommissionImportsList = React.lazy(() => import('@src/components/CarrierCommissions/Imports/List'));
const CreateCarrierCommissionImport = React.lazy(() => import('@src/components/CarrierCommissions/Imports/Create'));
const ShowCarrierCommissionImport = React.lazy(() => import('@src/components/CarrierCommissions/Imports/Show'));

const CategoryList = React.lazy(() => import('@src/components/categories/list'));
const CategoryEdit = React.lazy(() => import('@src/components/categories/edit'));
const CategoryCreate = React.lazy(() => import('@src/components/categories/create'));

const CategorizationList = React.lazy(() => import('@src/components/categorizations/list'));

const ContactFormsList = React.lazy(() => import('@src/components/contact_forms/list'));
const ShowContactForm = React.lazy(() => import('@src/components/contact_forms/show'));
const QuoteInquiriesList = React.lazy(() => import('@src/components/contact_forms/QuoteInquiriesList'));
const HolidayGiftsList = React.lazy(() => import('@src/components/holiday_gifts/list'));
const HolidayGiftsEdit = React.lazy(() => import('@src/components/holiday_gifts/edit'));

const CreateRateAndRuleManual = React.lazy(() => import('@src/components/guides/RateAndRuleManuals/create'));
const EditRateAndRuleManual = React.lazy(() => import('@src/components/guides/RateAndRuleManuals/edit'));
const RateAndRuleManualsList = React.lazy(() => import('@src/components/guides/RateAndRuleManuals/list'));
const CreateUnderwritingGuideline = React.lazy(() => import('@src/components/guides/UnderwritingGuidelines/create'));
const EditUnderwritingGuideline = React.lazy(() => import('@src/components/guides/UnderwritingGuidelines/edit'));
const UnderwritingGuidelinesList = React.lazy(() => import('@src/components/guides/UnderwritingGuidelines/list'));
const CreateProgramGuide = React.lazy(() => import('@src/components/guides/ProgramGuides/create'));
const EditProgramGuide = React.lazy(() => import('@src/components/guides/ProgramGuides/edit'));
const ProgramGuidesList = React.lazy(() => import('@src/components/guides/ProgramGuides/list'));

const FieldManagementNewsList = React.lazy(() => import('@src/components/field_management_news/list'));
const FieldManagementNewsCreate = React.lazy(() => import('@src/components/field_management_news/create'));
const FieldManagementNewsEdit = React.lazy(() => import('@src/components/field_management_news/edit'));

const HomeBannersList = React.lazy(() => import('@src/components/home_banners/list'));
const EditHomeBanner = React.lazy(() => import('@src/components/home_banners/edit'));
const CreateHomeBanner = React.lazy(() => import('@src/components/home_banners/create'));

const HomeLinksList = React.lazy(() => import('@src/components/home_links/list'));
const CreateHomeLink = React.lazy(() => import('@src/components/home_links/create'));
const EditHomeLink = React.lazy(() => import('@src/components/home_links/edit'));

const HomeArticlesList = React.lazy(() => import('@src/components/home_articles/list'));

const FeedItemsList = React.lazy(() => import('@src/components/feed_items/list'));

const MulesoftExportsList = React.lazy(() => import('@src/components/mulesoft_exports/list'));
const AnnouncementsExportsList = React.lazy(() => import('@src/components/announcements_exports/list'));

const SocialSharingsList = React.lazy(() => import('@src/components/collateral_social_media/List'));
const CreateSocialSharing = React.lazy(() => import('@src/components/collateral_social_media/Create'));
const EditSocialSharing = React.lazy(() => import('@src/components/collateral_social_media/edit'));

const TrainingsList = React.lazy(() => import('@src/components/training/trainings/list'));
const CreateTraining = React.lazy(() => import('@src/components/training/trainings/create'));
const EditTraining = React.lazy(() => import('@src/components/training/trainings/edit'));

const JobAidsList = React.lazy(() => import('@src/components/training/jobaids/list'));
const CreateJobAid = React.lazy(() => import('@src/components/training/jobaids/create'));
const EditJobAid = React.lazy(() => import('@src/components/training/jobaids/edit'));

const WebinarsList = React.lazy(() => import('@src/components/training/webinars/list'));
const CreateWebinar = React.lazy(() => import('@src/components/training/webinars/create'));
const EditWebinar = React.lazy(() => import('@src/components/training/webinars/edit'));

const AlertsList = React.lazy(() => import('@src/components/alerts/list'));
const AlertsCreate = React.lazy(() => import('@src/components/alerts/create'));
const AlertsEdit = React.lazy(() => import('@src/components/alerts/edit'));

const EditRSMAgent360 = React.lazy(() => import('@src/components/rsm-agent360/Edit'));
const RSMAgent360List = React.lazy(() => import('@src/components/rsm-agent360/List'));

const AdminsList = React.lazy(() => import('@src/components/users/admins/List'));
const AgentsList = React.lazy(() => import('@src/components/users/agents/List'));
const CreateAdmin = React.lazy(() => import('@src/components/users/admins/Create'));
const CreateHomeOfficeEmployee = React.lazy(() => import('@src/components/users/home_office_employees/Create'));
const EditAdmin = React.lazy(() => import('@src/components/users/admins/Edit'));
const EditHomeOfficeEmployee = React.lazy(() => import('@src/components/users/home_office_employees/Edit'));
const HomeOfficeEmployeesList = React.lazy(() => import('@src/components/users/home_office_employees/List'));
const ShowAgent = React.lazy(() => import('@src/components/users/agents/Show'));

const AgenciesList = React.lazy(() => import('@src/components/agencies/list'));
const EditAgency = React.lazy(() => import('@src/components/agencies/edit'));

const AgencyLocationsList = React.lazy(() => import('@src/components/agency_locations/list'));
const EditAgencyLocation = React.lazy(() => import('@src/components/agency_locations/edit'));

const CustomerListsList = React.lazy(() => import('@src/components/customer_lists/list'));
const EditCustomerList = React.lazy(() => import('@src/components/customer_lists/edit'));

const RedirectsList = React.lazy(() => import('@src/components/redirects/list'));
const RedirectsCreate = React.lazy(() => import('@src/components/redirects/create'));
const RedirectsEdit = React.lazy(() => import('@src/components/redirects/edit'));

const RolesList = React.lazy(() => import('@src/components/roles/list'));
const CreateRole = React.lazy(() => import('@src/components/roles/create'));
const EditRole = React.lazy(() => import('@src/components/roles/edit'));

const BrandsList = React.lazy(() => import('@src/components/brands/list'));
const CreateBrand = React.lazy(() => import('@src/components/brands/create'));
const EditBrand = React.lazy(() => import('@src/components/brands/edit'));

const CoBrandedVideoCategoriesList = React.lazy(() => import('@src/components/co_branded_video_categories/list'));
const CreateCoBrandedVideoCategory = React.lazy(() => import('@src/components/co_branded_video_categories/create'));
const EditCoBrandedVideoCategory = React.lazy(() => import('@src/components/co_branded_video_categories/edit'));

const CoBrandedVideoLogosList = React.lazy(() => import('@src/components/co_branded_video_logos/list'));
const CreateCoBrandedVideoLogo = React.lazy(() => import('@src/components/co_branded_video_logos/create'));
const EditCoBrandedVideoLogo = React.lazy(() => import('@src/components/co_branded_video_logos/edit'));

const CoBrandedVideoTemplatesList = React.lazy(() => import('@src/components/co_branded_video_templates/list'));
const CreateCoBrandedVideoTemplate = React.lazy(() => import('@src/components/co_branded_video_templates/create'));
const EditCoBrandedVideoTemplate = React.lazy(() => import('@src/components/co_branded_video_templates/edit'));

const CoBrandedVideoRequestsList = React.lazy(() => import('@src/components/co_branded_video_requests/list'));
const EditCoBrandedVideoRequest = React.lazy(() => import('@src/components/co_branded_video_requests/edit'));

const CollateralLogosList = React.lazy(() => import('@src/components/collateral_logos/list'));
const CreateCollateralLogo = React.lazy(() => import('@src/components/collateral_logos/create'));
const EditCollateralLogo = React.lazy(() => import('@src/components/collateral_logos/edit'));

const CollateralCategoriesList = React.lazy(() => import('@src/components/collateral_categories/list'));
const CreateCollateralCategory = React.lazy(() => import('@src/components/collateral_categories/create'));
const EditCollateralCategory = React.lazy(() => import('@src/components/collateral_categories/edit'));

const CollateralPricesList = React.lazy(() => import('@src/components/collateral_prices/list'));
const CreateCollateralPrice = React.lazy(() => import('@src/components/collateral_prices/create'));
const EditCollateralPrice = React.lazy(() => import('@src/components/collateral_prices/edit'));

const CollateralTemplatesList = React.lazy(() => import('@src/components/collateral_templates/list'));
const CreateCollateralTemplate = React.lazy(() => import('@src/components/collateral_templates/create'));
const EditCollateralTemplate = React.lazy(() => import('@src/components/collateral_templates/edit'));

const CollateralDigitalAssetsList = React.lazy(() => import('@src/components/collateral_digital_assets/list'));
const CreateCollateralDigitalAsset = React.lazy(() => import('@src/components/collateral_digital_assets/create'));
const EditCollateralDigitalAsset = React.lazy(() => import('@src/components/collateral_digital_assets/edit'));

const CollateralRequestsList = React.lazy(() => import('@src/components/collateral_requests/list'));
const CreateCollateralRequest = React.lazy(() => import('@src/components/collateral_requests/create'));
const EditCollateralRequest = React.lazy(() => import('@src/components/collateral_requests/edit'));

const CollateralTemplateSuggestionsList = React.lazy(() => import('@src/components/collateral_template_suggestions/list'));
const ShowCollateralTemplateSuggestion = React.lazy(() => import('@src/components/collateral_template_suggestions/show'));

const TagsList = React.lazy(() => import('@src/components/tags/list'));
const CreateTag = React.lazy(() => import('@src/components/tags/create'));
const EditTag = React.lazy(() => import('@src/components/tags/edit'));

const PremierAccessGroupsList = React.lazy(() => import('@src/components/premier_access_groups/list'));
const ShowPremierAccessGroup = React.lazy(() => import('@src/components/premier_access_groups/show'));
const PremierAccessGroupSpendingList = React.lazy(() => import('@src/components/premier_access_groups/spendingList'));
const PremierAccessGroupUserSpendingList = React.lazy(() => import('@src/components/premier_access_groups/spendingUserList'));

const CreatePremierAccessGroupsImport = React.lazy(() => import('@src/components/premier_access_groups_imports/create'));
const PremierAccessGroupsImportsList = React.lazy(() => import('@src/components/premier_access_groups_imports/list'));
const ShowPremierAccessGroupsImport = React.lazy(() => import('@src/components/premier_access_groups_imports/show'));

const CreateUserProductsAuthorityImport = React.lazy(() => import('@src/components/user_products_authority_imports/create'));
const UserProductsAuthorityImportsList = React.lazy(() => import('@src/components/user_products_authority_imports/list'));
const ShowUserProductsAuthorityImport = React.lazy(() => import('@src/components/user_products_authority_imports/show'));

const CreateImportUsersHistory = React.lazy(() => import('@src/components/import_users_histories/create'));
const ShowImportUsersHistory = React.lazy(() => import('@src/components/import_users_histories/show'));
const ImportUsersHistoriesList = React.lazy(() => import('@src/components/import_users_histories/list'));

const ItemExpirationsList = React.lazy(() => import('@src/components/expirations/by-item/list'));
const PageExpirationsList = React.lazy(() => import('@src/components/expirations/by-page/list'));

const ImagesList = React.lazy(() => import('@src/components/images/list'));
const EditImage = React.lazy(() => import('@src/components/images/edit'));

// Without a dashboard react-admin lands on the first resource, which the admin may not be allowed to open
const Dashboard = () => <Navigate replace to={getFirstAllowedPath()} />;

const App = () => {
    const authenticated = useAuth();

    // Every resource below picks its components from the permissions of the signed in
    // admin, so this tree must not be evaluated before those permissions are loaded
    if (!authenticated) {
        return <AuthLoader />;
    }

    return (
        <SessionInactivityCheck>
            <Admin
                disableTelemetry
                lightTheme={lightTheme}
                darkTheme={darkTheme}
                layout={LayoutCustom}
                catchAll={Error404}
                dashboard={Dashboard}
                dataProvider={dataProvider}
                i18nProvider={i18nProvider}
            >
                <Resource
                    name="featured_content"
                    list={Permissions('home_articles', 'read') === true ? HomeArticlesList : Error404}
                />
                <Resource
                    name="feed_items"
                    list={Permissions('feed_items', 'read') === true ? FeedItemsList : Error404}
                />
                <Resource
                    name="admin_notifications"
                />
                <Resource
                    name="business_units"
                    list={BusinessUnitsList}
                />
                <Resource
                    name="mulesoft_exports"
                    list={Permissions('mulesoft_exports', 'read') === true ? MulesoftExportsList : Error404}
                />
                <Resource
                    name="announcements_exports"
                    list={Permissions('announcements_exports', 'read') === true ? AnnouncementsExportsList : Error404}
                />
                <Resource
                    name="product_updates"
                    list={Permissions('product_updates', 'read') === true ? ProductUpdateList : Error404}
                    create={Permissions('product_updates', 'create') === true ? CreateProductUpdate : Error404}
                    edit={Permissions('product_updates', 'update') === true ? EditProductUpdate : Error404}
                />
                <Resource
                    name="contact_forms"
                    list={Permissions('form_entries', 'read') === true ? ContactFormsList : Error404}
                    show={Permissions('form_entries', 'read') === true ? ShowContactForm : Error404}
                />
                <Resource
                    name="products"
                />
                <Resource
                    name="platforms"
                    list={Permissions('products', 'read') === true ? PlatformsList : Error404}
                    edit={Permissions('products', 'update') === true ? EditPlatform : Error404}
                />
                <Resource
                    name="all_products"
                    list={Permissions('products', 'read') === true ? ProductAvailabilityList : Error404}
                    edit={Permissions('products', 'update') === true ? EditProductAvailability : Error404}
                />
                <Resource
                    name="product_pages"
                    list={Permissions('products', 'read') === true ? ProductPagesList : Error404}
                    create={Permissions('products', 'update') === true ? CreateProductPage : Error404}
                    edit={Permissions('products', 'update') === true ? EditProductPage : Error404}
                />
                <Resource
                    name="states"
                />
                <Resource
                    name="sales_channels"
                />
                <Resource
                    name="carrier_commissions/imports"
                    create={CreateCarrierCommissionImport}
                    list={CarrierCommissionImportsList}
                    show={ShowCarrierCommissionImport}
                />
                <Resource
                    name="carrier_commissions"
                    list={CarrierCommissionsList}
                />
                <Resource
                    name="home_links"
                    list={Permissions('home_links', 'read') === true ? HomeLinksList : Error404}
                    create={Permissions('home_links', 'create') === true ? CreateHomeLink : Error404}
                    edit={Permissions('home_links', 'update') === true ? EditHomeLink : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.fieldManagementUpdates}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.productNewsAndUpdates}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.jobAids}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.systemAndTechnical}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.safetyTips}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name={`taxonomies/${Taxonomies.holidayAndSeasonal}/categories`}
                    list={Permissions('categories', 'read') === true ? CategoryList : Error404}
                    create={Permissions('categories', 'create') === true ? CategoryCreate : Error404}
                    edit={Permissions('categories', 'update') === true ? CategoryEdit : Error404}
                />
                <Resource
                    name="categorizations"
                    list={CategorizationList}
                />
                <Resource
                    list={Permissions('rate_bulletins', 'read') === true ? RateAndRuleManualsList : Error404}
                    create={Permissions('rate_bulletins', 'create') === true ? CreateRateAndRuleManual : Error404}
                    edit={Permissions('rate_bulletins', 'update') === true ? EditRateAndRuleManual : Error404}
                    name="rate_bulletins"
                />
                <Resource
                    name="underwriting_guidelines"
                    list={Permissions('underwriting_guidelines', 'read') === true ? UnderwritingGuidelinesList : Error404}
                    edit={Permissions('underwriting_guidelines', 'update') === true ? EditUnderwritingGuideline : Error404}
                    create={Permissions('underwriting_guidelines', 'create') === true ? CreateUnderwritingGuideline : Error404}
                />
                <Resource
                    name="program_guides"
                    list={Permissions('program_guides', 'read') === true ? ProgramGuidesList : Error404}
                    edit={Permissions('program_guides', 'update') === true ? EditProgramGuide : Error404}
                    create={Permissions('program_guides', 'create') === true ? CreateProgramGuide : Error404}
                />
                <Resource
                    name="trainings"
                    list={Permissions('trainings', 'read') === true ? TrainingsList : Error404}
                    edit={Permissions('trainings', 'update') === true ? EditTraining : Error404}
                    create={Permissions('trainings', 'create') === true ? CreateTraining : Error404}
                />
                <Resource
                    name="job_aids"
                    list={Permissions('job_aids', 'read') === true ? JobAidsList : Error404}
                    edit={Permissions('job_aids', 'update') === true ? EditJobAid : Error404}
                    create={Permissions('job_aids', 'create') === true ? CreateJobAid : Error404}
                />
                <Resource
                    name="webinars"
                    list={Permissions('webinars', 'read') === true ? WebinarsList : Error404}
                    edit={Permissions('webinars', 'update') === true ? EditWebinar : Error404}
                    create={Permissions('webinars', 'create') === true ? CreateWebinar : Error404}
                />
                <Resource
                    name="field_management_news"
                    list={FieldManagementNewsList}
                    edit={FieldManagementNewsEdit}
                    create={FieldManagementNewsCreate}
                />
                <Resource
                    name="rsm_agent360"
                    list={RSMAgent360List}
                    edit={EditRSMAgent360}
                />
                <Resource
                    name="alerts"
                    list={Permissions('alerts', 'read') === true ? AlertsList : Error404}
                    edit={Permissions('alerts', 'update') === true ? AlertsEdit : Error404}
                    create={Permissions('alerts', 'create') === true ? AlertsCreate : Error404}
                />
                <Resource
                    name="home_banners"
                    list={Permissions('home_banners', 'read') === true ? HomeBannersList : Error404}
                    edit={Permissions('home_banners', 'update') === true ? EditHomeBanner : Error404}
                    create={Permissions('home_banners', 'create') === true ? CreateHomeBanner : Error404}
                />
                <Resource
                    name="admins"
                    list={Permissions('users', 'read') === true ? AdminsList : Error404}
                    create={Permissions('users', 'create') === true ? CreateAdmin : Error404}
                    edit={Permissions('users', 'update') === true ? EditAdmin : Error404}
                />
                <Resource
                    name="employees"
                    list={Permissions('users', 'read') === true ? HomeOfficeEmployeesList : Error404}
                    create={Permissions('users', 'create') === true ? CreateHomeOfficeEmployee : Error404}
                    edit={Permissions('users', 'update') === true ? EditHomeOfficeEmployee : Error404}
                />
                <Resource
                    name="agents"
                    list={Permissions('users', 'read') === true ? AgentsList : Error404}
                    show={Permissions('users', 'read') === true ? ShowAgent : Error404}
                />
                <Resource
                    name="agencies"
                    list={Permissions('users', 'read') === true ? AgenciesList : Error404}
                    edit={Permissions('users', 'update') === true ? EditAgency : Error404}
                />
                <Resource
                    name="quote_inquiries"
                    list={QuoteInquiriesList}
                    show={ShowContactForm}
                />
                <Resource
                    name="holiday_gift_requests"
                    list={Permissions('form_entries', 'read') === true ? HolidayGiftsList : Error404}
                    edit={Permissions('form_entries', 'update') === true ? HolidayGiftsEdit : Error404}
                />
                <Resource
                    name="images"
                    list={Permissions('images', 'read') === true ? ImagesList : Error404}
                    edit={Permissions('images', 'update') === true ? EditImage : Error404}
                />
                <Resource
                    name="attachments"
                    list={AttachmentsList}
                    edit={EditAttachment}
                />
                <Resource
                    name="brands"
                    list={Permissions('co_branded_videos', 'read') === true ? BrandsList : Error404}
                    create={Permissions('co_branded_videos', 'create') === true ? CreateBrand : Error404}
                    edit={Permissions('co_branded_videos', 'update') === true ? EditBrand : Error404}
                />
                <Resource
                    name="co_branded_video_categories"
                    list={Permissions('co_branded_videos', 'read') === true ? CoBrandedVideoCategoriesList : Error404}
                    create={Permissions('co_branded_videos', 'create') === true ? CreateCoBrandedVideoCategory : Error404}
                    edit={Permissions('co_branded_videos', 'update') === true ? EditCoBrandedVideoCategory : Error404}
                />
                <Resource
                    name="co_branded_video_logos"
                    list={Permissions('co_branded_videos', 'read') === true ? CoBrandedVideoLogosList : Error404}
                    create={Permissions('co_branded_videos', 'create') === true ? CreateCoBrandedVideoLogo : Error404}
                    edit={Permissions('co_branded_videos', 'update') === true ? EditCoBrandedVideoLogo : Error404}
                />
                <Resource
                    name="co_branded_video_templates"
                    list={Permissions('co_branded_videos', 'read') === true ? CoBrandedVideoTemplatesList : Error404}
                    create={Permissions('co_branded_videos', 'create') === true ? CreateCoBrandedVideoTemplate : Error404}
                    edit={Permissions('co_branded_videos', 'update') === true ? EditCoBrandedVideoTemplate : Error404}
                />
                <Resource
                    name="co_branded_video_requests"
                    list={Permissions('co_branded_videos', 'read') === true ? CoBrandedVideoRequestsList : Error404}
                    edit={Permissions('co_branded_videos', 'update') === true ? EditCoBrandedVideoRequest : Error404}
                />
                <Resource
                    name="collateral_categories"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralCategoriesList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralCategory : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralCategory : Error404}
                />
                <Resource
                    name="collateral_prices"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralPricesList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralPrice : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralPrice : Error404}
                />
                <Resource
                    name="collateral_logos"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralLogosList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralLogo : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralLogo : Error404}
                />
                <Resource
                    name="collateral_templates"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralTemplatesList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralTemplate : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralTemplate : Error404}
                />
                <Resource
                    name="collateral_digital_assets"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralDigitalAssetsList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralDigitalAsset : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralDigitalAsset : Error404}
                />
                <Resource
                    name="social_sharings"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? SocialSharingsList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateSocialSharing : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditSocialSharing : Error404}
                />
                <Resource
                    name="collateral_requests"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralRequestsList : Error404}
                    create={Permissions('co_branded_marketing_collaterals', 'create') === true ? CreateCollateralRequest : Error404}
                    edit={Permissions('co_branded_marketing_collaterals', 'update') === true ? EditCollateralRequest : Error404}
                />
                <Resource
                    name="collateral_template_suggestions"
                    list={Permissions('co_branded_marketing_collaterals', 'read') === true ? CollateralTemplateSuggestionsList : Error404}
                    show={Permissions('co_branded_marketing_collaterals', 'read') === true ? ShowCollateralTemplateSuggestion : Error404}
                />
                <Resource
                    name="impacted_policies"
                    list={Permissions('impacting_policies', 'read') === true ? ImpactingPolicyList : Error404}
                    create={Permissions('impacting_policies', 'create') === true ? CreateImpactingPolicy : Error404}
                    edit={Permissions('impacting_policies', 'update') === true ? EditImpactingPolicy : Error404}
                />
                <Resource
                    name="item_expirations"
                    list={Permissions('page_expirations', 'read') === true ? ItemExpirationsList : Error404}
                />
                <Resource
                    name="page_expirations"
                    list={Permissions('page_expirations', 'read') === true ? PageExpirationsList : Error404}
                />
                <Resource name="writing_companies" />
                <Resource name="reassignment" />
                <Resource
                    name="redirects"
                    list={Permissions('redirects', 'read') === true ? RedirectsList : Error404}
                    edit={Permissions('redirects', 'update') === true ? RedirectsEdit : Error404}
                    create={Permissions('redirects', 'create') === true ? RedirectsCreate : Error404}
                />
                <Resource
                    name="roles"
                    list={Permissions('roles', 'read') === true ? RolesList : Error404}
                    create={Permissions('roles', 'create') === true ? CreateRole : Error404}
                    edit={Permissions('roles', 'update') === true ? EditRole : Error404}
                />
                <Resource
                    name="tags"
                    list={Permissions('tags', 'read') === true ? TagsList : Error404}
                    create={Permissions('tags', 'create') === true ? CreateTag : Error404}
                    edit={Permissions('tags', 'update') === true ? EditTag : Error404}
                />
                <Resource
                    name="search_phrases"
                    list={Permissions('search_settings', 'read') === true ? SearchPhraseList : Error404}
                    create={Permissions('search_settings', 'create') === true ? SearchPhraseCreate : Error404}
                    edit={Permissions('search_settings', 'update') === true ? SearchPhraseEdit : Error404}
                    show={Permissions('search_settings', 'read') === true ? SearchPhraseShow : Error404}
                />
                <Resource
                    name="matching_searches"
                    list={Permissions('search_settings', 'read') === true ? MatchingSearchList : Error404}
                    create={Permissions('search_settings', 'create') === true ? MatchingSearchCreate : Error404}
                    edit={Permissions('search_settings', 'update') === true ? MatchingSearchEdit : Error404}
                />
                <Resource
                    name="search_terms"
                    list={Permissions('search_settings', 'read') === true ? SearchTermsList : Error404}
                />
                <Resource
                    name="premier_access_groups"
                    list={Permissions('users', 'read') === true ? PremierAccessGroupsList : Error404}
                    show={Permissions('users', 'read') === true ? ShowPremierAccessGroup : Error404}
                />
                <Resource
                    name="premier_access_groups_spending"
                    list={Permissions('users', 'read') === true ? PremierAccessGroupSpendingList : Error404}
                />
                <Resource
                    name="premier_access_group_users_spending"
                    list={Permissions('users', 'read') === true ? PremierAccessGroupUserSpendingList : Error404}
                />
                <Resource
                    name="premier_access_groups_imports"
                    create={Permissions('users', 'create') === true ? CreatePremierAccessGroupsImport : Error404}
                    list={Permissions('users', 'read') === true ? PremierAccessGroupsImportsList : Error404}
                    show={Permissions('users', 'read') === true ? ShowPremierAccessGroupsImport : Error404}
                />
                <Resource
                    name="user_products_authority_imports"
                    create={Permissions('users', 'create') === true ? CreateUserProductsAuthorityImport : Error404}
                    list={Permissions('users', 'read') === true ? UserProductsAuthorityImportsList : Error404}
                    show={Permissions('users', 'read') === true ? ShowUserProductsAuthorityImport : Error404}
                />
                <Resource
                    name="import_users_histories"
                    create={Permissions('users', 'create') === true ? CreateImportUsersHistory : Error404}
                    list={Permissions('users', 'read') === true ? ImportUsersHistoriesList : Error404}
                    show={Permissions('users', 'show') === true || Permissions('users', 'read') === true ? ShowImportUsersHistory : Error404}
                />
                <Resource
                    name="agency_locations"
                    list={Permissions('users', 'read') === true ? AgencyLocationsList : Error404}
                    edit={Permissions('users', 'update') === true ? EditAgencyLocation : Error404}
                />
                <Resource
                    name="customer_lists"
                    list={Permissions('users', 'read') === true ? CustomerListsList : Error404}
                    edit={Permissions('users', 'update') === true ? EditCustomerList : Error404}
                />
                <Resource
                    name="tracked_downloads"
                    list={Permissions('tracked_downloads', 'read') === true ? TrackedDownloadsList : Error404}
                />

                <Resource
                    name="banners"
                    list={Permissions('promo_notification_settings', 'read') === true ? BannersList : Error404}
                    edit={Permissions('promo_notification_settings', 'update') === true ? EditBanner : Error404}
                />

                <CustomRoutes>
                    <Route path="/unauthorized" element={<Error401 />} />
                    <Route path="/session_timeouts" element={<SessionsSettingEdit />} />
                    <Route path="/search_weights" element={<SearchWeightsEdit />} />
                    <Route path="/admins/import" element={<ImportAdmins />} />
                    <Route path="/employees/import" element={<ImportHomeOfficeEmployees />} />
                </CustomRoutes>
            </Admin>
        </SessionInactivityCheck>
    );
};

export default App;
